import type { FetcherMiddleware } from '@funtech-int/web-toolkit';

import type { Logger } from '@kinopoisk-int/logger';
import { responseMessageFormatter } from '@kinopoisk-int/logger';

export function fetcherLoggingMiddleware(logger: Logger, requestType: string): FetcherMiddleware {
    return async (req, next) => {
        const { url } = req;
        const startTime = Date.now();

        logger.info({ url, type: requestType }, 'Request started');

        try {
            const response = await next(req);
            logger.info(
                {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    headers: Object.fromEntries((new Headers(req.headers) as any).entries()),
                },
                responseMessageFormatter({
                    type: requestType,
                    url,
                    requestTime: Date.now() - startTime,
                    status: response.status,
                })
            );
            return response;
        } catch (error) {
            if (error instanceof Error) {
                logger.error({ url, type: requestType }, error.message);
            }
            throw error;
        }
    };
}

const MOCKSERVICE_NAME = 'mockservice';

const MOCKSERVICE_BACKEND_NAME_HEADER_NAME = `${MOCKSERVICE_NAME}-backend-name`;
const MOCKSERVICE_GRAPHQL_OPERATIONS_HEADER_NAME = `${MOCKSERVICE_NAME}-graphql-operation`;
const MOCKSERVICE_GRAPHQL_VARIABLES_HEADER_NAME = `${MOCKSERVICE_NAME}-graphql-variables`;
const MOCKSERVICE_MOCK_UNIQ_KEY_HEADER_NAME = `${MOCKSERVICE_NAME}-mock-uniq-key`;
const MOCKSERVICE_TEST_RUN_ID_HEADER_NAME = `${MOCKSERVICE_NAME}-test-run-id`;
const MOCKSERVICE_COOKIE_NAME = MOCKSERVICE_NAME;
const MOCKSERVICE_GRAPHQL_OPERATIONS_COOKIE_NAME = `${MOCKSERVICE_NAME}-graphql-operations`;
const MOCKSERVICE_TEST_RUN_ID_COOKIE_NAME = `${MOCKSERVICE_NAME}-test-run-id`;

interface CurrentMockingGraphQlOperationFromCookieParams {
    cookie?: string;
    operationName: string;
}
function getCurrentMockingGraphQlOperationFromCookie({
    cookie = '',
    operationName,
}: CurrentMockingGraphQlOperationFromCookieParams): string | null {
    const mockingItems = cookie ? cookie.split(',') : null;

    if (!mockingItems) {
        return null;
    }

    const currentMockedOperation = mockingItems.find(
        (mockedOperation) => operationName === mockedOperation
    );

    return currentMockedOperation || null;
}

export function getMockserviceHeadersMiddleware(
    mockserviceApiOrigin: string,
    cookies: Record<string, string | undefined>
): FetcherMiddleware {
    return (req, next) => {
        if (Object.keys(cookies).length === 0) {
            return next(req);
        }

        const mockServiceCookieValue = cookies[MOCKSERVICE_COOKIE_NAME];
        if (!mockServiceCookieValue) {
            return next(req);
        }

        const url = new URL(req.url);

        const operationName = url.searchParams.get('operationName');
        if (!operationName) {
            return next(req);
        }

        const currentMockedOperation = getCurrentMockingGraphQlOperationFromCookie({
            cookie: cookies[MOCKSERVICE_GRAPHQL_OPERATIONS_COOKIE_NAME],
            operationName,
        });

        const mockHeaders: Record<string, string> = {};
        if (currentMockedOperation) {
            mockHeaders[MOCKSERVICE_GRAPHQL_OPERATIONS_HEADER_NAME] = currentMockedOperation;
            mockHeaders[MOCKSERVICE_GRAPHQL_VARIABLES_HEADER_NAME] = encodeURIComponent(
                JSON.stringify({})
            );
            mockHeaders[MOCKSERVICE_MOCK_UNIQ_KEY_HEADER_NAME] = mockServiceCookieValue;
        }
        if (cookies[MOCKSERVICE_TEST_RUN_ID_COOKIE_NAME]) {
            mockHeaders[MOCKSERVICE_TEST_RUN_ID_HEADER_NAME] =
                cookies[MOCKSERVICE_TEST_RUN_ID_COOKIE_NAME]!;
        }
        mockHeaders[MOCKSERVICE_BACKEND_NAME_HEADER_NAME] = 'kp-graphql-router';

        const mergedHeaders = new Headers(req.headers);
        Object.entries(mockHeaders).forEach(([key, value]) => {
            mergedHeaders.set(key, value);
        });

        const mockserviceUrl = new URL(mockserviceApiOrigin);

        url.protocol = mockserviceUrl.protocol;
        url.host = mockserviceUrl.host;
        url.port = mockserviceUrl.port;

        const request = new Request(url, { ...req, headers: mergedHeaders });

        return next(request);
    };
}
